import { CheckOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { Button, Grid, Modal, Space, Typography, message, theme } from 'antd';
import { useState } from 'react';
import {
  approveBudgetApi,
  generateOrderFromBudgetApi,
  getBudgetSummaryApi,
} from 'api/hallazgoFlujo';
import { stageOf } from 'assets/data/estadosPresupuesto';
import { buildApprovalOrderTasks } from './buildApprovalOrderTasks';

const { useToken } = theme;
const { Text } = Typography;

function InfoTile({ icon, tint, title, description }) {
  const { token } = useToken();
  return (
    <div
      style={{
        display: 'flex',
        gap: token.marginSM,
        alignItems: 'flex-start',
        padding: token.paddingSM,
        borderRadius: token.borderRadiusLG,
        background: tint + '14',
        border: `1px solid ${tint}33`,
      }}
    >
      <span style={{ color: tint, fontSize: 18, marginTop: 2 }}>{icon}</span>
      <div>
        <Text strong style={{ color: tint }}>
          {title}
        </Text>
        <br />
        <Text type="secondary" style={{ fontSize: 13 }}>
          {description}
        </Text>
      </div>
    </div>
  );
}

function BudgetApprovalModal({ target, onClose, onSuccess }) {
  const { token } = useToken();
  const [submitting, setSubmitting] = useState(false);
  // Bajo `md` los cuatro botones del pie no entran en una fila: antd los alinea
  // a la derecha y quedan desordenados al envolver. Apilados y a ancho completo
  // se leen enteros, con la acción principal arriba (column-reverse invierte el
  // orden del array, que va de menos a más importante).
  const screens = Grid.useBreakpoint();
  const compact = !screens.md;

  if (!target) return null;

  const isPendingApproval = stageOf(target) === 'esperando_aprobar';
  const title = isPendingApproval ? 'Aprobar Presupuesto' : 'Crear Orden de Trabajo';

  const submit = async ({ crearItems, crearOrden }) => {
    setSubmitting(true);
    try {
      // Paso 1: aprobar el presupuesto SOLO si aún está pendiente de aprobación.
      // Si el modal se abrió sobre un budget ya aprobado (aprobado_sin_odt),
      // saltamos este paso y vamos directo a crear la ODT.
      if (isPendingApproval) {
        const approveResult = await approveBudgetApi(target.idBudget);
        if (approveResult?.codeMessage) {
          message.error(approveResult.message || 'Error al aprobar el presupuesto');
          return;
        }
      }

      // Paso 2 (opcional): crear ODT
      if (!crearOrden) {
        message.success('Presupuesto aprobado');
        onSuccess?.();
        onClose?.();
        return;
      }

      let tasks = [];
      if (crearItems) {
        // Leemos del SUMMARY (no de /get-items-budget crudo): el summary expone
        // los equipos del N:M `budgetitem_ascensor` en `it.Ascensores`, que es
        // donde viven los equipos desde el plan trazabilidad-equipos (la columna
        // legacy `elevator` queda NULL). buildApprovalOrderTasks prefiere
        // id_ascensores (FK) y cae a elevator string para items legacy.
        const summary = await getBudgetSummaryApi(target.idBudget);
        if (!summary || summary.state === 'error' || !Array.isArray(summary.items)) {
          message.error(summary?.message || 'No se pudieron cargar los items del presupuesto');
          return;
        }
        tasks = buildApprovalOrderTasks(summary.items);
      }

      const orderResult = await generateOrderFromBudgetApi({
        idBudget: target.idBudget,
        tasks,
      });

      if (orderResult?.idOrder) {
        const msg = crearItems
          ? `Presupuesto aprobado y ODT #${orderResult.idOrder} creada con items`
          : `Presupuesto aprobado y ODT #${orderResult.idOrder} creada`;
        message.success(msg);
        // Se entrega el id para que el padre ofrezca planificar la ODT en la carta Gantt.
        onSuccess?.({ idOrder: orderResult.idOrder });
        onClose?.();
      } else {
        // Approve OK pero ODT falló: advertir y cerrar igual (el estado ya cambió en el backend)
        message.warning(
          `Presupuesto aprobado, pero no se pudo crear la ODT: ${
            orderResult?.message || 'Error desconocido'
          }`
        );
        onSuccess?.();
        onClose?.();
      }
    } catch (err) {
      message.error('Error al procesar el presupuesto');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      title={
        <Space>
          <CheckOutlined style={{ color: token.colorSuccess }} />
          <span>{title}</span>
        </Space>
      }
      onCancel={onClose}
      footer={
        <div
          style={{
            display: 'flex',
            flexDirection: compact ? 'column-reverse' : 'row',
            justifyContent: 'flex-end',
            gap: 8,
          }}
        >
          {[
            <Button key="cancel" onClick={onClose} disabled={submitting} block={compact}>
              Cancelar
            </Button>,
            isPendingApproval && (
              <Button
                key="only-approve"
                block={compact}
                onClick={() => submit({ crearItems: false, crearOrden: false })}
                loading={submitting}
              >
                Solo aprobar (ODT después)
              </Button>
            ),
            <Button
              key="create-without-items"
              block={compact}
              onClick={() => submit({ crearItems: false, crearOrden: true })}
              loading={submitting}
            >
              Crear sin items
            </Button>,
            <Button
              key="create-with-items"
              block={compact}
              type="primary"
              onClick={() => submit({ crearItems: true, crearOrden: true })}
              loading={submitting}
            >
              Crear con items
            </Button>,
          ].filter(Boolean)}
        </div>
      }
      width={560}
      destroyOnHidden
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div
          style={{
            padding: token.paddingSM,
            borderRadius: token.borderRadiusLG,
            background: token.colorPrimaryBg,
            border: `1px solid ${token.colorPrimaryBorder}`,
          }}
        >
          <Text strong>Presupuesto #{target.idBudget}</Text>
          <br />
          <Text type="secondary">Cliente: {target.name}</Text>
        </div>

        <Text strong>
          {isPendingApproval
            ? '¿Crear la orden de trabajo al aprobar?'
            : '¿Crear la orden con los items del presupuesto?'}
        </Text>

        <InfoTile
          icon={<CheckOutlined />}
          tint={token.colorSuccess}
          title="Crear con items del presupuesto"
          description="Se crea la ODT con todos los items (descripción, cantidades y valores unitarios)."
        />
        <InfoTile
          icon={<ClockCircleOutlined />}
          tint={token.colorWarning}
          title="Crear sin items"
          description="La ODT se crea vacía; los items se agregan después manualmente."
        />
        {isPendingApproval && (
          <InfoTile
            icon={<ClockCircleOutlined />}
            tint={token.colorInfo}
            title="Solo aprobar (ODT después)"
            description={
              'Marca el presupuesto como aprobado sin crear ODT. Se puede crear más tarde desde "Crear ODT".'
            }
          />
        )}
      </Space>
    </Modal>
  );
}

export default BudgetApprovalModal;
